import React from 'react'
import { ControllingRow } from '@myapp/contract'
import { TableColumn } from '../../../../shared/ui'
import { formatDateDDMMYYYY } from '../../../../shared/helpers/date'

export const SCHEDULE_LABELS: Record<string, string> = {
  WITHOUT_FOLLOW_UP: 'Ohne Folgetermin',
  WITH_FOLLOW_UPS: 'Mit Folgeterminen',
  RESCHEDULED_BY_OFFICE: 'Umnominiert (Büro)',
  RESCHEDULED_BY_TECHNICIAN: 'Umnominiert (HW)',
}

export function fmtNum(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function fmtHours(planned: number, actual: number): string {
  return `${fmtNum(planned)}/${fmtNum(actual)} Std.`
}

export function renderSollIst(planned: number, actual: number): React.ReactElement {
  return (
    <div className="leading-tight whitespace-nowrap">
      <div className="text-gray-500">
        <span className="text-[10px] uppercase tracking-wide mr-1">Soll</span>
        {fmtNum(planned)}
      </div>
      <div className="text-gray-900 font-medium">
        <span className="text-[10px] uppercase tracking-wide mr-1">Ist</span>
        {fmtNum(actual)}
      </div>
    </div>
  )
}

export function fmtDeviation(dev: number): string {
  return `${dev >= 0 ? '+' : ''}${dev.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
}

export function fmtDeviationHours(dev: number): string {
  return `${dev >= 0 ? '+' : ''}${fmtNum(dev)} Std.`
}

export function deviationClass(dev: number): string {
  if (dev > 5) return 'text-red-600 font-medium'
  if (dev < -5) return 'text-green-600 font-medium'
  return ''
}

export function deviationHoursClass(dev: number): string {
  if (dev > 0) return 'text-red-600 font-medium'
  if (dev < 0) return 'text-green-600 font-medium'
  return ''
}

function formatTechnicianTrades(ids: number[], labels?: Map<number, string>): string {
  if (!ids.length) return '–'
  return ids.map((id) => labels?.get(id) ?? `#${id}`).join(', ')
}

export interface ControllingColumnsOpts {
  gewerkLabels?: Map<number, string>
}

export function createControllingColumns(
  opts: ControllingColumnsOpts = {},
): TableColumn<ControllingRow>[] {
  const { gewerkLabels } = opts
  return [
    {
      key: 'workOrderId',
      header: 'Auftrags-ID',
      sortKey: 'workOrderId',
      headerClassName: 'min-w-[75px]',
      render: (r) => <span className="font-medium">{r.workOrderId}</span>,
    },
    {
      key: 'scheduleStatus',
      header: 'Terminstatus',
      headerClassName: 'min-w-[90px]',
      render: (r) => SCHEDULE_LABELS[r.scheduleStatus] ?? r.scheduleStatus,
    },
    {
      key: 'reasons',
      header: 'Grund',
      headerClassName: 'min-w-[55px]',
      cellClassName: 'align-top',
      render: (r) =>
        r.reasons.length === 0 ? (
          <span className="text-gray-400">–</span>
        ) : (
          <ul className="leading-tight space-y-0.5">
            {r.reasons.map((reason, i) => (
              <li key={i}>{reason}</li>
            ))}
          </ul>
        ),
    },
    {
      key: 'repairDate',
      header: 'Reparaturtermin',
      sortKey: 'repairDate',
      headerClassName: 'min-w-[100px]',
      cellClassName: 'whitespace-nowrap',
      render: (r) =>
        r.repairDateFrom === r.repairDateTo
          ? formatDateDDMMYYYY(r.repairDateFrom)
          : `${formatDateDDMMYYYY(r.repairDateFrom)} - ${formatDateDDMMYYYY(r.repairDateTo)}`,
    },
    {
      key: 'building',
      header: 'Gebäude',
      sortKey: 'building',
      headerClassName: 'min-w-[95px]',
      render: (r) => r.building,
    },
    {
      key: 'tenant',
      header: 'Mieter',
      sortKey: 'tenant',
      headerClassName: 'min-w-[80px]',
      render: (r) => r.tenant,
    },
    {
      key: 'gewerkId',
      header: 'Gewerk-Nr',
      sortKey: 'gewerkId',
      headerClassName: 'min-w-[70px]',
      cellClassName: 'text-right whitespace-nowrap',
      render: (r) => (r.gewerkId == null ? <span className="text-gray-400">–</span> : r.gewerkId),
    },
    {
      key: 'trade',
      header: 'Auftrag Gewerk',
      sortKey: 'trade',
      headerClassName: 'min-w-[95px]',
      render: (r) => r.trade,
    },
    {
      key: 'technicianTrades',
      header: 'Handwerker Gewerke',
      headerClassName: 'min-w-[110px]',
      render: (r) => formatTechnicianTrades(r.technicianTradeIds, gewerkLabels),
    },
    {
      key: 've',
      header: 'VE',
      sortKey: 've',
      headerClassName: 'min-w-[35px]',
      render: (r) => r.ve,
    },
    {
      key: 'technician',
      header: 'Handwerker',
      sortKey: 'technician',
      headerClassName: 'min-w-[85px]',
      cellClassName: 'whitespace-normal break-words',
      render: (r) => r.technicians.join(', '),
    },
    {
      key: 'workHours',
      header: 'Arbeit (Std)',
      sortKey: 'actualWorkHours',
      headerClassName: 'min-w-[75px]',
      render: (r) => renderSollIst(r.plannedWorkHours, r.actualWorkHours),
    },
    {
      key: 'travelHours',
      header: 'Fahrt (Std)',
      sortKey: 'actualTravelHours',
      headerClassName: 'min-w-[75px]',
      render: (r) => renderSollIst(r.plannedTravelHours, r.actualTravelHours),
    },
    {
      key: 'totalHours',
      header: 'Gesamt (Std)',
      sortKey: 'actualTotalHours',
      headerClassName: 'min-w-[80px]',
      render: (r) => renderSollIst(r.plannedTotalHours, r.actualTotalHours),
    },
    {
      key: 'deviation',
      header: 'Abweichung',
      sortKey: 'deviationHours',
      headerClassName: 'min-w-[90px]',
      cellClassName: 'whitespace-nowrap text-right',
      render: (r) => (
        <div className="leading-tight whitespace-nowrap">
          <div className={`text-gray-500 ${deviationClass(r.deviation)}`}>
            {fmtDeviation(r.deviation)}
          </div>
          <div className={`font-medium ${deviationHoursClass(r.deviationHours)}`}>
            {fmtDeviationHours(r.deviationHours)}
          </div>
        </div>
      ),
    },
    {
      key: 'wertEuro',
      header: 'Wert (€)',
      sortKey: 'wertEuro',
      headerClassName: 'min-w-[70px]',
      cellClassName: 'whitespace-nowrap text-right',
      render: (r) =>
        r.wertEuro == null ? (
          <span className="text-gray-400">–</span>
        ) : (
          `${fmtNum(r.wertEuro)} €`
        ),
    },
  ]
}

/** Legacy static export kept for back-compat with existing imports. */
export const CONTROLLING_COLUMNS = createControllingColumns()
